import React from 'react';
import { Page } from 'components/ui/page';

/* ══════════════════════════════════════════════════════════════════════════
   Compatibility layer over `Page`.

   Twenty files wrap their content in `PageContainer`, which had its own
   padding, no max-width cap and — critically — no vertical rhythm. Pages using
   it relied on each child supplying its own bottom margin, which is why
   spacing between a page header, a toolbar and a table differed from screen to
   screen.

   It now renders `Page`, so every screen gets the same gutters, the same
   max-width and the same `gap` between blocks. Call sites are unchanged.

   New code should import `Page` directly.
   ══════════════════════════════════════════════════════════════════════════ */

interface PageContainerProps {
    children: React.ReactNode;
    /**
     * Legacy prop. It built a class name by interpolation (`max-w-${maxWidth}`),
     * which Tailwind's scanner cannot see, so it never emitted any CSS — no
     * call site passes it. Mapped onto `Page`'s width scale rather than
     * silently ignored.
     */
    maxWidth?: '7xl' | '6xl' | '5xl' | 'full';
    className?: string;
}

const WIDTH_MAP = {
    '7xl': 'wide',
    '6xl': 'wide',
    '5xl': 'narrow',
    full: 'wide',
} as const;

export const PageContainer: React.FC<PageContainerProps> = ({
    children,
    maxWidth = 'full',
    className = '',
}) => (
    <Page width={WIDTH_MAP[maxWidth] ?? 'wide'} className={className}>
        {children}
    </Page>
);

export default PageContainer;

export interface PageContentLayoutProps {
    mobileContent: React.ReactNode;
    desktopContent: React.ReactNode;
    breakpoint?: 'md' | 'lg' | 'xl';
}

const BP: Record<NonNullable<PageContentLayoutProps['breakpoint']>, { mobile: string; desktop: string }> = {
    md: { mobile: 'md:hidden', desktop: 'hidden md:block' },
    lg: { mobile: 'lg:hidden', desktop: 'hidden lg:block' },
    xl: { mobile: 'xl:hidden', desktop: 'hidden xl:block' },
};

export const PageContentLayout: React.FC<PageContentLayoutProps> = ({
    mobileContent,
    desktopContent,
    breakpoint = 'lg',
}) => {
    const bp = BP[breakpoint];
    return (
        <>
            <div className={bp.mobile}>{mobileContent}</div>
            <div className={bp.desktop}>{desktopContent}</div>
        </>
    );
};

export const PageCardGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className = '',
}) => (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${className}`}>
        {children}
    </div>
);
